import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";

export default function NewsFeedModal({ onClose }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  const modalRef = useRef(null);
  const [drag, setDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [position, setPosition] = useState({ top: 20, left: 20 });
const formatContent = (text) => {
  if (!text) return null;

  return text.split("\n").map((line, i) => {
    const trimmedLine = line.trim();

    const isBullet = trimmedLine.startsWith("* ") && !trimmedLine.startsWith("**");

    const boldRegex = /\*\*(.*?)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(line)) !== null) {
      parts.push(line.substring(lastIndex, match.index));
      parts.push(<strong key={`bold-${i}-${match.index}`} style={{ color: "#fff", fontWeight: "bold" }}>{match[1]}</strong>);
      lastIndex = boldRegex.lastIndex;
    }
    parts.push(line.substring(lastIndex));

    const finalLineContent = parts.length > 0 ? parts : line;

    if (isBullet) {
      let bulletText = finalLineContent;

      if (Array.isArray(bulletText) && typeof bulletText[0] === 'string') {
        bulletText = [bulletText[0].replace(/^\s*\*\s+/, ""), ...bulletText.slice(1)];
      } else if (typeof bulletText === 'string') {
        bulletText = bulletText.replace(/^\s*\*\s+/, "");
      }

      return (
        <div key={i} style={{ 
          display: "flex", 
          paddingLeft: "15px", 
          marginBottom: "6px", 
          alignItems: "flex-start" 
        }}>
          <span style={{ color: "#50e3c2", marginRight: "10px", lineHeight: "1.2" }}>•</span>
          <div style={{ flex: 1 }}>{bulletText}</div>
        </div>
      );
    }

    return (
      <div key={i} style={{ marginBottom: "10px", minHeight: "1em" }}>
        {finalLineContent}
      </div>
    );
  });
};

  useEffect(() => {
    const q = query(
      collection(db, "Global_News"),
      orderBy("timestamp", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setNews(newsData);
      setLoading(false);
    }, (err) => {
      console.error("News Fetch Error:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleMouseDown = (e) => {
    setZIndex(getNextZ() + 10000);
    const rect = modalRef.current.getBoundingClientRect();
    setDrag({
      isDragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!drag.isDragging) return;
      setPosition({
        top: e.clientY - drag.offsetY,
        left: e.clientX - drag.offsetX,
      });
    };
    const handleMouseUp = () => setDrag(prev => ({ ...prev, isDragging: false }));

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [drag]);

const getTypeStyles = (type) => {
    switch (type) {
      case 'game update': 
        return { color: '#f1c40f' };
      case 'market': 
        return { color: '#3498db' };
      case 'event': 
        return { color: '#9b59b6' };
      case 'community': 
        return { color: '#e67e22' };
      case 'general':
      default: 
        return { color: '#50e3c2' };
    }
  };
const modalContent = (
    <div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: "500px", 
        height: "500px",
        zIndex,
        transform: "none",
        border: "1px solid #50e3c233",
        background: "#050808",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: "0 0 40px rgba(0,0,0,0.9)",
        fontFamily: "'JetBrains Mono', monospace"
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="draggable-title" style={{ 
        background: "rgba(80, 227, 194, 0.03)", 
        padding: "6px 12px",
        borderBottom: "1px solid #50e3c211",
        flexShrink: 0 
      }}>
        <span style={{ letterSpacing: "1px", fontSize: "9px", color: "#50e3c299" }}>
          NEWS
        </span>
        <button onClick={onClose} style={{ fontSize: "14px", opacity: 1.0 }}>×</button>
      </div>

      <div className="scrollable-content" style={{ 
        flexGrow: 1, 
        overflowY: "auto", 
        padding: "20px", 
        display: "flex",
        flexDirection: "column",
        gap: "25px" 
      }}>
        {loading ? (
          <p className="blink" style={{ color: "#50e3c2", textAlign: "center", fontSize: "10px" }}>LOADING...</p>
        ) : (
          news.map((item, index) => {
            const typeStyle = getTypeStyles(item.type);
            return (
              <div key={item.id} style={{ position: "relative" }}>

                <div style={{ 
                  width: "100%", 
                  height: "3px", 
                  background: typeStyle.color, 
                  marginBottom: "8px",
                  boxShadow: `0 0 10px ${typeStyle.color}44`
                }} />

                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  marginBottom: "6px",
                  fontSize: "9px",
                  fontStyle: "italic",
                  color: "#444"
                }}>
                  <span style={{ color: typeStyle.color, fontWeight: "bold", fontStyle: "normal" }}>
                    {item.type.toUpperCase()}
                  </span>
                  <span>{item.displayDate}</span>
                </div>

                <h2 style={{ 
                  color: "#fff", 
                  fontSize: "12px", 
                  fontWeight: "bold", 
                  margin: "0 0 6px 0",
                  letterSpacing: "0.5px"
                }}>
                  {item.headline}
                </h2>

                <div style={{ 
                  color: "#bbbbbb", 
                  fontSize: "10px", 
                  lineHeight: "1.5",
                  paddingLeft: "2px"
                }}>
                  {formatContent(item.content)}
                </div>
              </div>
            );
          })
        )}
        
        <div style={{ textAlign: "center", padding: "20px 0", color: "#1a1a1a", fontSize: "8px" }}>
          END OF FEED
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}